import { logError } from '~/common/utils/log';
import { registerChannelAuthHandler } from '~/config/initPusher';

import { authenticateGamificationChannel } from '../services/gamification.service.api';

/**
 * Auth handler for gamification private channels (`private-gamification-user-{userId}`,
 * where `userId` is the platform user id — the `id` claim / `UserContext.userId`).
 *
 * Mirrors the live-activity handler shape: the Pusher SDK provides `socketId` +
 * `channelName`; we POST them to the gamification-service auth endpoint (Bearer token
 * attached by the axios interceptor) and hand the resulting `{ auth }` back to Pusher.
 * The server signs only the caller's own channel, so no extra ownership checks here.
 */
const gamificationAuthHandler = (
  socketId: string,
  channelName: string,
  callback: (error: Error | null, authInfo?: { auth: string; channel_data?: string }) => void
): void => {
  (async () => {
    try {
      const authData = await authenticateGamificationChannel({
        socket_id: socketId,
        channel_name: channelName,
      });
      callback(null, authData);
    } catch (error) {
      logError('Gamification Pusher auth failed:', error, { channel: channelName });
      callback(error instanceof Error ? error : new Error(String(error)));
    }
  })();
};

// Guard against duplicate registration (module may be imported from multiple places).
let isHandlerRegistered = false;

/**
 * Registers the gamification auth handler. Idempotent — safe to call multiple times.
 * Invoked at module load from `~/config/initPusher`.
 */
export const registerGamificationAuthHandler = (): void => {
  if (isHandlerRegistered) {
    return;
  }

  // Match the per-user gamification channel: private-gamification-user-{userId}
  const gamificationChannelPattern = /^private-gamification-user-/;

  registerChannelAuthHandler(gamificationChannelPattern, gamificationAuthHandler);
  isHandlerRegistered = true;
};
